import React from 'react';
import { Field, Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { FormFields, FormSection, RegistrationFormWrapper, ImageBackground, FormWrapper, Price, IBANWrapper, TShirtCardButton, TShirtSelector, TShirtSizes, TShirtSizeButton, TShirtUnavailableTooltip } from './styles';
import { useSearchParams } from 'react-router-dom';
import { createValidateForm } from './validation';
import { HeaderComponent } from '../Header/Header';
import Button from '../Button/Button';
import axios from 'axios';
import { getData } from 'country-list';
import { euConversionRate, products, tShirtImages } from '../../config/constants';
import { useTranslation } from 'react-i18next';
import RaceCountdown from '../RaceCountdown/RaceCountdown';
import TierProgress, { ParticipationTier } from './TierProgress';

type Distance = 14 | 27;

export interface FormValues {
    email: string;
    name: string;
    distance: Distance;
    gender: string;
    termsAndConditions: boolean;
    birth: string;
    country: string;
    team?: string;
    itraId?: string;
    phoneNumber: string;
    withTShirt: boolean;
    tShirtSize: '' | 'XS' | 'S' | 'M' | 'L' | 'XL';
    paid: boolean;
    discountCode: string;
}

const RegistrationForm = () => {
    React.useEffect(() => { window.scrollTo(0, 0); }, []);
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const [serverError, setServerError] = React.useState<string | null>(null);
    const [discountPercent, setDiscountPercent] = React.useState<number>(0);
    const [discountCodeChecked, setDiscountCodeChecked] = React.useState<boolean>(false);
    const [discountCodeInactive, setDiscountCodeInactive] = React.useState<boolean>(false);
    const [discountLimitUses, setDiscountLimitUses] = React.useState<number | null>(null);
    const [spacesLeft, setSpacesLeft] = React.useState<number | null>(null);
    const [isRegistrationFull, setIsRegistrationFull] = React.useState<boolean>(false);
    const [tierPricing, setTierPricing] = React.useState<{ fees: Record<Distance, number>; tshirt: number } | null>(null);
    const [tiers, setTiers] = React.useState<ParticipationTier[]>([]);
    const [currentCount, setCurrentCount] = React.useState<number>(0);
    const [registrationOpeningTime, setRegistrationOpeningTime] = React.useState<Date | null>(null);
    const [isRegistrationOpeningTimeLoaded, setIsRegistrationOpeningTimeLoaded] = React.useState<boolean>(false);
    const [distance, setDistance] = React.useState<Distance>(Number(searchParams.get('product')) as Distance || 27);
    const isTestMode = (searchParams.get('test') || '').toLowerCase().trim() === 'true';
    const uniqueCode = searchParams.get('uniqueCode') || '';
    const observePointCode = searchParams.get('observePoint') || '';
    const emailParam = searchParams.get('email') || '';
    const selectedProduct = products.find(product => product.distance === distance);
    const price = isTestMode
        ? (selectedProduct?.testProductPrice || selectedProduct?.price || 0)
        : (tierPricing?.fees[distance] ?? selectedProduct?.price ?? 0);
    const isRegistrationOpeningTimeInFuture = registrationOpeningTime !== null
        && registrationOpeningTime.getTime() > Date.now();
    const formattedRegistrationOpeningDate = registrationOpeningTime
        ? `${String(registrationOpeningTime.getDate()).padStart(2, '0')}.${String(registrationOpeningTime.getMonth() + 1).padStart(2, '0')}.${registrationOpeningTime.getFullYear()}`
        : '';

    const tShirtPrice = tierPricing ? tierPricing.tshirt : (selectedProduct?.tShirtPrice || 0);
    const initialValues: FormValues = {
        email: emailParam,
        name: '',
        distance: distance as Distance,
        gender: '',
        termsAndConditions: false,
        birth: '',
        country: 'BG',
        team: '',
        itraId: '',
        withTShirt: false,
        tShirtSize: '',
        phoneNumber: '',
        paid: false,
        discountCode: uniqueCode
    };
    const apiUrl = process.env.REACT_APP_REGISTRATION_API_URL;
    const validateRegistrationForm = React.useMemo(() => createValidateForm(t), [t]);
    const countries = React.useMemo(() => {
        const displayNames = new Intl.DisplayNames([i18n.resolvedLanguage ?? i18n.language], { type: 'region' });
        return getData()
            .map(({ code, name }) => ({ code, name: displayNames.of(code) ?? name }))
            .sort((first, second) => first.name.localeCompare(second.name, i18n.language));
    }, [i18n.language, i18n.resolvedLanguage]);
    const fallbackTShirtImage = 'https://pvmolqp98bhv9my7.public.blob.vercel-storage.com/product-box-image.png';
    const resetServerError = () => {
        setServerError(null);
    }

    const handleEmailBlur = async (email: string, setFieldValue: (field: string, value: unknown) => void) => {
        if (!apiUrl || !email || uniqueCode) return;
        try {
            const response = await axios.post(`${apiUrl}/check-email`, { email });
            if (response.data?.eligible === true) {
                setFieldValue('name', response.data.name ?? '');
                setFieldValue('birth', String(response.data.birth ?? ''));
                setFieldValue('gender', response.data.gender ?? '');
                setFieldValue('team', response.data.team ?? '');
                setDiscountPercent(response.data.longDistanceWinner ? 100 : 10);
            }
        } catch {
            // not eligible or network error — no action needed
        }
    };

    const fetchDiscountForCode = React.useCallback(async (code: string) => {
        const trimmedCode = code.trim();
        if (!trimmedCode || !apiUrl) {
            setDiscountPercent(0);
            setDiscountCodeInactive(false);
            setDiscountCodeChecked(false);
            setDiscountLimitUses(null);
            return;
        }

        try {
            const response = await axios.get(`${apiUrl}/discount-code`, {
                params: { code: trimmedCode },
            });
            const isValid = response.data?.valid === true;
            const isInactive = response.data?.inactive === true;
            const discount = Number(response.data?.discount ?? 0);
            const limitUses = response.data?.limitUses;
            setDiscountPercent(isValid ? Math.max(0, Math.min(100, discount)) : 0);
            setDiscountCodeInactive(isInactive);
            setDiscountCodeChecked(true);
            setDiscountLimitUses(limitUses === null || limitUses === undefined ? null : Number(limitUses));
        } catch (error) {
            console.error('Discount code validation failed:', error, trimmedCode);
            setDiscountPercent(0);
            setDiscountCodeInactive(false);
            setDiscountCodeChecked(true);
            setDiscountLimitUses(null);
        }
    }, [apiUrl]);

    React.useEffect(() => {
        fetchDiscountForCode(uniqueCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiUrl, uniqueCode]);

    React.useEffect(() => {
        const fetchParticipantsAvailability = async () => {
            if (!apiUrl || isTestMode) return;
            try {
                const response = await axios.get(`${apiUrl}/get-participants-count`);
                setSpacesLeft(Number(response.data?.spacesLeft ?? 0));
                setIsRegistrationFull(response.data?.isFull === true);
                setTierPricing(response.data?.pricing ?? null);
                setTiers(Array.isArray(response.data?.tiers) ? response.data.tiers : []);
                setCurrentCount(Number(response.data?.currentCount ?? 0));
            } catch (error) {
                console.error('Failed to fetch participants availability:', error);
            }
        };

        fetchParticipantsAvailability();
    }, [apiUrl, isTestMode]);

    React.useEffect(() => {
        setRegistrationOpeningTime(null);
        setIsRegistrationOpeningTimeLoaded(false);
        if (!apiUrl) {
            setIsRegistrationOpeningTimeLoaded(true);
            return;
        }

        let isActive = true;
        const fetchRegistrationOpeningTime = async () => {
            try {
                const response = await axios.get(`${apiUrl}/registration-open-date`);
                const dateValue = process.env.REACT_APP_REGISTRATION_OPEN_DATE ?? response.data?.registrationOpenDate;
                if (typeof dateValue !== 'string') return;

                const openingTime = new Date(
                    /^\d{4}-\d{2}-\d{2}$/.test(dateValue) ? `${dateValue}T00:00:00` : dateValue
                );
                if (isActive && !Number.isNaN(openingTime.getTime())) {
                    setRegistrationOpeningTime(openingTime);
                }
            } catch (error) {
                console.error('Failed to fetch registration opening date:', error);
            } finally {
                if (isActive) {
                    setIsRegistrationOpeningTimeLoaded(true);
                }
            }
        };

        fetchRegistrationOpeningTime();
        return () => {
            isActive = false;
        };
    }, [apiUrl]);

    const handleImageFallback = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const image = event.currentTarget;
        if (image.src !== fallbackTShirtImage) {
            image.src = fallbackTShirtImage;
        }
    };

    const EmailParamEffect: React.FC = () => {
        const { setFieldValue } = useFormikContext();
        React.useEffect(() => {
            if (emailParam) {
                handleEmailBlur(emailParam, setFieldValue);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
        return null;
    };

    interface PaymentDetailsProps {
        basePrice: number;
        tShirtPrice: number;
        withTShirt: boolean;
        discountPercent: number;
    }

    const PaymentDetails: React.FC<PaymentDetailsProps> = ({ basePrice, tShirtPrice, withTShirt, discountPercent }) => {
    const appliedDiscount = basePrice * (Math.max(0, Math.min(100, discountPercent)) / 100);
    const discountedBase = Math.max(0, basePrice - appliedDiscount);
    const total = discountedBase + (withTShirt ? tShirtPrice : 0);

        return (
            <>
                <Price>{t('registration:payment.entryFee', { amount: basePrice })}</Price>
                {discountPercent > 0 && (
                    <Price>{t('registration:payment.discount', { discountPercent, amount: appliedDiscount })}</Price>
                )}
                {withTShirt && (
                    <Price>{t('registration:payment.tShirt', { amount: tShirtPrice })}</Price>
                )}
                <Price>{t('registration:payment.total', { amount: total })}</Price>
                <IBANWrapper>
                    {total === 0 ? (
                        <p>{t('registration:payment.freeRegistration')}</p>
                    ) : (
                        <>
                            <p>{t('registration:payment.redirectInfo')}</p>
                            <p>{t('registration:payment.confirmationInfo')}</p>
                        </>
                    )}
                </IBANWrapper>
            </>
        );
    }

    const handleRegistrationSubmit = async (
        values: FormValues,
        { setSubmitting }: FormikHelpers<FormValues>
    ) => {
        if (isRegistrationFull) {
            setServerError(t('registration:errors.registrationFull'));
            setSubmitting(false);
            return;
        }

        if (typeof apiUrl !== 'string' || apiUrl.length === 0) {
            console.error('Registration API URL is not defined.');
            setServerError(t('registration:errors.missingPaymentConfiguration'));
            setSubmitting(false);
            return;
        }

        try {
            const payload: Record<string, unknown> = {
                ...values,
            };

            if (isTestMode) {
                payload.test = 'true';
                payload.observePointCode = observePointCode;
            }

            const submittedCode = values.discountCode.trim();
            if (submittedCode.length > 0) {
                payload.uniqueCode = submittedCode;
            }
            delete payload.discountCode;

            const response = await axios.post(`${apiUrl}/create-checkout-session`, {
                data: payload,
            });
            const checkoutUrl = response.data?.checkoutUrl;

            if (typeof checkoutUrl !== 'string' || checkoutUrl.length === 0) {
                throw new Error('Missing checkout redirect URL');
            }

            resetServerError();
            window.location.assign(checkoutUrl);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                console.error('Email already registered with paid status:', error, values.email);
                setServerError(t('registration:errors.emailAlreadyPaid'));
            } else if (axios.isAxiosError(error) && error.response?.status === 402) {
                console.error('Email already registered but payment pending:', error, values.email);
                const retryUrl = `/register/retry-payment?email=${encodeURIComponent(values.email)}`;
                setServerError(t('registration:errors.paymentPending', { retryUrl }));
            } else if (axios.isAxiosError(error) && typeof error.response?.data?.error === 'string') {
                console.error('Registration validation error:', error, values.email);
                setServerError(error.response.data.error);
                if (error.response?.data?.code === 'max_participants_reached') {
                    setIsRegistrationFull(true);
                }
            } else {
                console.error('Registration error:', error, values.email);
                setServerError(t('registration:errors.checkoutRedirectFailed'));
            }
        } finally {
            setSubmitting(false);
        }
    };

  return (
    <RegistrationFormWrapper distance={distance}>
        <HeaderComponent hideDate />
        <ImageBackground image="https://pvmolqp98bhv9my7.public.blob.vercel-storage.com/Register_Background.jpg" />
            <FormWrapper>
                
                {isTestMode && (
                    <div className="server error">{t('registration:notices.testMode')}</div>
                )}
                {!isTestMode && spacesLeft !== null && (
                    <div className="server error">
                        {isRegistrationFull
                            ? t('registration:notices.registrationFull')
                            : t('registration:notices.spacesLeft', { count: spacesLeft })}
                    </div>
                )}
                {!isTestMode && tiers.length > 0 && (
                    <TierProgress tiers={tiers} currentCount={currentCount} distance={distance} />
                )}
                <a href="/register/payment">{t('registration:links.payment')}</a><br /><br />
                <a href="/participants">{t('registration:links.participants')}</a><br /><br />
                <a href="/results?year=2026">{t('registration:links.results2026')}</a><br /><br />
                {!isTestMode && !isRegistrationOpeningTimeLoaded ? (
                    <div className="server error">{t('registration:notices.checkingOpeningDate')}</div>
                ) : isRegistrationOpeningTimeInFuture ? (
                    <div className="server error">
                        {t('registration:notices.registrationOpensOn', { date: formattedRegistrationOpeningDate })}
                    </div>
                ) : !isTestMode && isRegistrationFull ? null : (
                <>
                {!isTestMode && uniqueCode && discountCodeChecked && discountPercent > 0 && (
                    <div className="server error">{t('registration:notices.activeDiscount', { discountPercent })}</div>
                )}
                {!isTestMode && uniqueCode && discountCodeChecked && discountCodeInactive && (
                    <div className="server error">{t('registration:notices.inactiveDiscount')}</div>
                )}
                {!isTestMode && uniqueCode && discountCodeChecked && !discountCodeInactive && discountPercent === 0 && (
                    <div className="server error">{t('registration:notices.invalidDiscount')}</div>
                )}
                <RaceCountdown />
                <Formik
                    initialValues={ initialValues }
                    validate={ validateRegistrationForm }
                    onSubmit={ handleRegistrationSubmit }
                >
                    {({
                        errors,
                        touched,
                        dirty,
                        handleSubmit,
                        handleBlur,
                        isSubmitting,
                        values,
                        setFieldValue,
                    }) => {
                        const appliedDiscount = price * (Math.max(0, Math.min(100, discountPercent)) / 100);
                        const discountedBase = Math.max(0, price - appliedDiscount);
                        const total = discountedBase + (values.withTShirt ? tShirtPrice : 0);

                        return (
                        <Form onChange={resetServerError}>
                            <EmailParamEffect />
                            <FormFields>
                                <FormSection>
                                    <label htmlFor="distance">{t('registration:fields.distance')}</label>
                                    <Field 
                                        as="select" 
                                        required 
                                        name="distance" 
                                        id="distance" 
                                        value={distance} 
                                        onChange={(e: { target: { value: number; }; }) => {
                                            const nextDistance = Number(e.target.value) as Distance;
                                            setDistance(nextDistance);
                                            setFieldValue('distance', nextDistance);
                                        }} 
                                    >
                                        <option value={14}>{t('registration:fields.distance14')}</option>
                                        <option value={26}>{t('registration:fields.distance26')}</option>
                                    </Field>

                                    <label htmlFor="email">{t('registration:fields.email')}</label>
                                    <Field
                                        id="email"
                                        name="email"
                                        placeholder={t('registration:fields.emailPlaceholder')}
                                        type="email"
                                        minLength={4}
                                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                            handleBlur(e);
                                            handleEmailBlur(e.target.value.trim().toLowerCase(), setFieldValue);
                                        }}
                                    />
                                    {errors.email && touched.email && <div className="error">{errors.email}</div>}

                                    <label htmlFor="name">{t('registration:fields.name')}</label>
                                    <Field id="name" name="name" placeholder={t('registration:fields.namePlaceholder')} />
                                    {errors.name && touched.name && <div className="error">{errors.name}</div>}
                                    <label htmlFor="phoneNumber">{t('registration:fields.phoneNumber')}</label>
                                    <Field
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        placeholder={t('registration:fields.phonePlaceholder')}
                                        required
                                    />
                                    {errors.phoneNumber && touched.phoneNumber && <div className="error">{errors.phoneNumber}</div>}
                                    <label htmlFor="gender">{t('registration:fields.gender')}</label>
                                    <Field as="select" name="gender" id="gender">
                                        <option value="" hidden></option>
                                        <option value="male">{t('registration:fields.genderMale')}</option>
                                        <option value="female">{t('registration:fields.genderFemale')}</option>
                                    </Field>
                                    {errors.gender && touched.gender && <div className="error">{errors.gender}</div>}
                                    <label htmlFor="birth">{t('registration:fields.birth')}</label>
                                    <Field id="birth" name="birth" type="date" required />
                                    {errors.birth && touched.birth && <div className="error">{errors.birth}</div>}
                                    <label htmlFor="country">{t('registration:fields.country')}</label>
                                    <Field as="select" id="country" name="country" required>
                                        {countries.map(({ code, name }) => (
                                            <option key={code} value={code}>{name}</option>
                                        ))}
                                    </Field>
                                    <label htmlFor="team">{t('registration:fields.team')} <span>{t('registration:fields.optional')}</span></label>
                                    <Field id="team" name="team" placeholder={t('registration:fields.team')} />
                                    <label htmlFor="itraId">{t('registration:fields.itraId')} <span>{t('registration:fields.optional')}</span></label>
                                    <Field id="itraId" name="itraId" maxLength={50} />
                                    <label htmlFor="discountCode">{t('registration:fields.discountCode')} <span>{t('registration:fields.optional')}</span></label>
                                    <Field
                                        id="discountCode"
                                        name="discountCode"
                                        placeholder={t('registration:fields.discountCodePlaceholder')}
                                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                            handleBlur(e);
                                            fetchDiscountForCode(e.target.value);
                                        }}
                                    />
                                    {!isTestMode && values.discountCode && discountCodeChecked && discountPercent > 0 && (
                                        <div className="error">{t('registration:notices.activeDiscount', { discountPercent })}</div>
                                    )}
                                    {!isTestMode && values.discountCode && discountCodeChecked && discountLimitUses !== null && (
                                        <div className="error">
                                            {discountLimitUses > 0
                                                ? t('registration:notices.discountUsesLeft', { count: discountLimitUses })
                                                : t('registration:notices.discountNoUsesLeft')}
                                        </div>
                                    )}
                                    {!isTestMode && values.discountCode && discountCodeChecked && discountCodeInactive && (
                                        <div className="error">{t('registration:notices.inactiveDiscount')}</div>
                                    )}
                                    {!isTestMode && values.discountCode && discountCodeChecked && !discountCodeInactive && discountPercent === 0 && (
                                        <div className="error">{t('registration:notices.invalidDiscount')}</div>
                                    )}
                                    <label className="checkbox-label" htmlFor="termsAndConditions">
                                        <Field
                                            type="checkbox"
                                            name="termsAndConditions"
                                            id="termsAndConditions"
                                            required
                                        />
                                        <span className="checkmark"></span>
                                        <p>{t('registration:terms')}</p>
                                    </label>
                                    {errors.termsAndConditions && touched.termsAndConditions && (
                                        <div className="error">{errors.termsAndConditions}</div>
                                    )}
                                </FormSection>
                                {/* <FormSection>
                                    <label>
                                        {t(tShirtPrice === 0 ? 'registration:tShirt.unavailableLabel' : 'registration:tShirt.label')}
                                    </label>
                                    <TShirtSelector>
                                        <TShirtUnavailableTooltip
                                            $disabled={tShirtPrice === 0}
                                            data-tooltip={t('registration:tShirt.unavailable')}
                                            tabIndex={tShirtPrice === 0 ? 0 : -1}
                                        >
                                            <TShirtCardButton
                                                type="button"
                                                disabled={tShirtPrice === 0}
                                                onClick={() => {
                                                    const nextSelected = !values.withTShirt;
                                                    setFieldValue('withTShirt', nextSelected);
                                                    if (!nextSelected) {
                                                        setFieldValue('tShirtSize', '');
                                                    }
                                                }}
                                                selected={values.withTShirt}
                                                grayscale={!values.withTShirt}
                                            >
                                                <img src={tShirtImages.front} alt={t('registration:tShirt.imageAlt')} onError={handleImageFallback} />
                                                <span className="cta">{values.withTShirt ? t('registration:tShirt.remove') : t('registration:tShirt.add')}</span>
                                                <span className="caption">
                                                    {values.withTShirt
                                                        ? t('registration:tShirt.addedCaption', { price: tShirtPrice })
                                                        : t('registration:tShirt.addCaption', { price: tShirtPrice })
                                                    }
                                                </span>
                                            </TShirtCardButton>
                                        </TShirtUnavailableTooltip>
                                    </TShirtSelector>
                                    <Field type="hidden" name="withTShirt" />
                                    <Field type="hidden" name="tShirtSize" />

                                    {values.withTShirt && (
                                        <>
                                            <label>{t('registration:tShirt.sizeLabel')}</label>
                                            <TShirtSizes>
                                                {(['XS', 'S', 'M', 'L', 'XL'] as const).map((size) => (
                                                    <TShirtSizeButton
                                                        key={size}
                                                        type="button"
                                                        selected={values.tShirtSize === size}
                                                        onClick={() => setFieldValue('tShirtSize', size)}
                                                    >
                                                        {size}
                                                    </TShirtSizeButton>
                                                ))}
                                            </TShirtSizes>
                                            {errors.tShirtSize && touched.withTShirt && (
                                                <div className="error">{errors.tShirtSize}</div>
                                            )}
                                        </>
                                    )}
                                    <Price>{t('registration:payment.currentTotal', { amount: total })}</Price>
                                </FormSection> */}
                            </FormFields>
                            {serverError && (
                                <div className="server error" dangerouslySetInnerHTML={{ __html: serverError }} />
                            )}
                            {errors && Object.keys(errors).length > 0 && (
                                <div className="error">
                                    {t('registration:notices.formHasErrors')}
                                </div>
                            )}
                            <PaymentDetails
                                basePrice={price}
                                tShirtPrice={tShirtPrice}
                                withTShirt={values.withTShirt}
                                discountPercent={discountPercent}
                            />
                            <Button
                                label={isSubmitting ? t('registration:payment.redirecting') : (total === 0 ? t('registration:payment.finishFree') : t('registration:payment.payAmount', { amount: total }))}
                                onClick={handleSubmit}
                                disabled={isSubmitting || isRegistrationFull}
                            />
                        </Form>
                    );
                }}
                </Formik>
                </>
                )}
            </FormWrapper> 
  </RegistrationFormWrapper>
  );
};
export default RegistrationForm;